import React, { useState, useEffect } from 'react'
import { Modal, Button, Input, Upload, Icon, Radio, Spin, Progress } from 'antd'
import './CreateOutfitModal.scss'

const { TextArea } = Input

/**
 * Modal for creating a new outfit using AI generation
 */
const CreateOutfitModal = ({
  visible,
  onClose,
  onSuccess,
  outfitDescription = '',
  outfitLabel = '',
  createOutfit,
  isCreating = false,
  creationProgress = 0
}) => {
  const [promptMode, setPromptMode] = useState('existing') // 'existing' or 'custom'
  const [customPrompt, setCustomPrompt] = useState('')
  const [referenceImages, setReferenceImages] = useState([]) // Array of { file, preview, id }

  // Reset state when modal opens
  useEffect(() => {
    if (visible) {
      setPromptMode(outfitDescription ? 'existing' : 'custom')
      setCustomPrompt('')
      setReferenceImages([])
    }
  }, [visible, outfitDescription])

  const handleReferenceUpload = file => {
    // Create preview
    const reader = new FileReader()
    reader.onload = e => {
      setReferenceImages(prev => [
        ...prev,
        {
          file,
          preview: e.target.result,
          id: Date.now() + Math.random() // Simple unique ID
        }
      ])
    }
    reader.readAsDataURL(file)
    return false
  }

  const handleRemoveReference = id => {
    setReferenceImages(prev => prev.filter(img => img.id !== id))
  }

  const handleCreate = async () => {
    const prompt = promptMode === 'existing' ? outfitDescription : customPrompt

    const result = await createOutfit({
      prompt,
      referenceImages: referenceImages.map(img => img.file)
    })

    if (result.success) {
      onSuccess(result.data)
      onClose()
    }
  }

  const currentPrompt =
    promptMode === 'existing' ? outfitDescription : customPrompt
  const isPromptValid = currentPrompt && currentPrompt.trim().length > 0

  // Parse outfit title from description (before colon) or use label
  const getOutfitTitle = () => {
    if (outfitDescription && outfitDescription.includes(':')) {
      return outfitDescription.split(':')[0].trim()
    }
    return outfitLabel || 'Custom Outfit'
  }

  return (
    <Modal
      visible={visible}
      title="Create Outfit"
      onCancel={onClose}
      footer={null}
      width={520}
      centered
      className="create-outfit-modal"
      maskClosable={!isCreating}
      closable={!isCreating}
    >
      {isCreating ? (
        <div className="creating-state">
          <Spin size="large" />
          <h3>Generating Outfit</h3>
          <Progress
            percent={creationProgress}
            status="active"
            strokeColor="#5057d5"
            showInfo={true}
          />
          <p>This may take up to a minute...</p>
        </div>
      ) : (
        <div className="create-outfit-content">
          {/* Outfit context */}
          {outfitLabel && (
            <div className="outfit-context">
              <span className="context-label">Creating outfit for:</span>
              <span className="context-value">{getOutfitTitle()}</span>
            </div>
          )}

          {/* Prompt mode selection */}
          <div className="form-section">
            <label className="section-label">Outfit Description</label>

            {outfitDescription && (
              <Radio.Group
                value={promptMode}
                onChange={e => setPromptMode(e.target.value)}
                className="prompt-mode-selector"
              >
                <Radio.Button value="existing">
                  Use Script Description
                </Radio.Button>
                <Radio.Button value="custom">Custom Prompt</Radio.Button>
              </Radio.Group>
            )}

            {promptMode === 'existing' && outfitDescription ? (
              <div className="existing-prompt-display">
                <p>{outfitDescription}</p>
              </div>
            ) : (
              <TextArea
                placeholder="Describe the outfit you want to create..."
                value={customPrompt}
                onChange={e => setCustomPrompt(e.target.value)}
                rows={4}
                autoSize={{ minRows: 3, maxRows: 6 }}
              />
            )}
          </div>

          {/* Reference image upload */}
          <div className="form-section">
            <label className="section-label">
              Reference Images <span className="optional">(Optional)</span>
            </label>

            {referenceImages.length > 0 ? (
              <div className="reference-images-list">
                <div className="image-grid">
                  {referenceImages.map(img => (
                    <div key={img.id} className="reference-preview-item">
                      <img src={img.preview} alt="Reference" />
                      <div
                        className="remove-btn"
                        onClick={() => handleRemoveReference(img.id)}
                      >
                        <Icon type="close" />
                      </div>
                    </div>
                  ))}
                  <Upload
                    showUploadList={false}
                    beforeUpload={handleReferenceUpload}
                    accept="image/*"
                    className="add-more-upload"
                  >
                    <div className="add-more-btn">
                      <Icon type="plus" />
                      <span>Add</span>
                    </div>
                  </Upload>
                </div>
              </div>
            ) : (
              <Upload
                showUploadList={false}
                beforeUpload={handleReferenceUpload}
                accept="image/*"
                className="reference-upload"
              >
                <div className="upload-area">
                  <Icon type="cloud-upload" />
                  <span>Click or drag images to upload</span>
                </div>
              </Upload>
            )}
          </div>

          {/* Actions */}
          <div className="modal-actions">
            <Button onClick={onClose}>Cancel</Button>
            <Button
              type="primary"
              onClick={handleCreate}
              disabled={!isPromptValid}
              icon="thunderbolt"
            >
              Generate Outfit
            </Button>
          </div>
        </div>
      )}
    </Modal>
  )
}

export default CreateOutfitModal
